import { useContext, useMemo } from 'react';
import moment from 'moment-timezone';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { colors } from '@karnott/colors';
import { CalendarIcon } from '@karnott/icons';
import { Table } from '@karnott/table';
import { pixelSpacing } from '@karnott/theme';
import { sharedSessionQueryOptions } from '../api/queries/sessions';
import TheLoader from '../components/Loader';
import Map from '../components/map/Map';
import { useHoveredParcelsStyle } from '../components/map/assets/Parcels/effects';
import Controls from '../components/map/controls/Controls';
import { useParcelSelection } from '../components/map/drawParcel/effects';
import { StatsSimpleNumericValue, StatsTitle } from '../components/reports/ui';
import { USER_CONSTANTS } from '../constants';
import { EQUIPMENT_CONSTANTS } from '../constants/equipmentConstants';
import { I18nContext } from '../contexts/I18nProvider';
import logo from '../images/karnott_logo.svg';
import { selectFullScreenMap } from '../slices/ui';
import { useAppSelector } from '../store/hooks';
import { DetailsMapContainer } from '../ui';
import { Logo } from '../ui/Header';
import { family, spacing } from '../ui/theme';
import { formatArea, formatDistance, formatDuration, formatUnit } from '../utils';
import { equipmentTypeLabel, getEquipmentIcon, getUnitWording } from '../utils/equipments';

function Header({ Icon, equipmentModel, date }) {
  const { t } = useContext(I18nContext);

  return (
    <ShareHeader>
      <Logo to="/">
        <img alt="karnott" src={logo} />
      </Logo>
      <IconWithLabel>
        {Icon ? <Icon size={28} color={colors('black')} /> : null}
        <span>{t(equipmentTypeLabel(equipmentModel))}</span>
      </IconWithLabel>
      <IconWithLabel>
        <CalendarIcon size={20} color={colors('black')} />
        <span>{moment(date).format('LL')}</span>
      </IconWithLabel>
    </ShareHeader>
  );
}

function ShareContainer() {
  const { t } = useContext(I18nContext);

  const fullScreenMap = useAppSelector(selectFullScreenMap);

  const id = useMemo(() => {
    const url = new URL(window.location.href);
    const searchParams = new URLSearchParams(url.search);
    return searchParams.get('id');
  }, []);

  const { data: session, error, isLoading } = useQuery(sharedSessionQueryOptions(id));

  const units = useMemo(
    () => ({
      area: USER_CONSTANTS.UNITS.DISTANCE_AND_AREA.SI,
      distance: USER_CONSTANTS.UNITS.DISTANCE_AND_AREA.SI,
      time: USER_CONSTANTS.UNITS.TIME.DECIMAL2,
    }),
    [],
  );
  const [area, concernedParcel, distance, equipmentModel, date, duration, parcelSessions, track, unit] = useMemo(
    () => [
      formatArea(session?.area, units.area, true),
      session?.concernedParcel,
      formatDistance(session?.distance, units.distance, true),
      session?.equipmentModel,
      session?.date,
      formatDuration(session?.motorDuration, units.time, true, true),
      session?.parcelSessions,
      session?.track,
      session?.equipmentGpioDefaultUnitMethod === EQUIPMENT_CONSTANTS.GPIO_METHODS.TIME
        ? formatDuration(session.unit, units.time, true)
        : formatUnit(session?.unit),
    ],
    [session, units],
  );

  const [, focusedIDs, toggle, clear] = useParcelSelection();

  const onRowEnter = (parcelId) => {
    clear();
    toggle({ id: parcelId });
  };

  const mapOverlays = useMemo(() => [<Controls key="0" />], []);
  const tracks = useMemo(() => track, [track]);
  const parcelsStyleOverrides = useHoveredParcelsStyle(focusedIDs);
  const Icon = useMemo(() => getEquipmentIcon(equipmentModel), [equipmentModel]);

  if (session && Object.keys(session).length) {
    if (parcelSessions) {
      return (
        <ShareComponent>
          {Object.keys(session).length ? (
            <StyledShareContainer>
              <Header Icon={Icon} equipmentModel={equipmentModel} date={date} />
              <ContentRow>
                <DetailsMapContainer fullScreenMap={fullScreenMap} fullHeight>
                  <Map
                    fitMask="parcel,tracks"
                    tracks={tracks}
                    parcels={concernedParcel}
                    overlays={mapOverlays}
                    parcelsStyleOverrides={parcelsStyleOverrides}
                  />
                </DetailsMapContainer>
                <DataContainer>
                  <ShareStatsContentBloc>
                    <div>
                      <StatsTitle>{t('Commons.motor_duration')}</StatsTitle>
                      <StatsSimpleNumericValue>{duration}</StatsSimpleNumericValue>
                    </div>
                    <div>
                      <StatsTitle>{t('Commons.traveled_distance')}</StatsTitle>
                      <StatsSimpleNumericValue>{distance}</StatsSimpleNumericValue>
                    </div>
                    <div>
                      <StatsTitle>{t('Commons.worked_area')}</StatsTitle>
                      <StatsSimpleNumericValue>{area}</StatsSimpleNumericValue>
                    </div>
                    {unit ? (
                      <div>
                        <StatsTitle>
                          {getUnitWording(t, equipmentModel, session.equipmentGpioDefaultUnitMethod)}
                        </StatsTitle>
                        <StatsSimpleNumericValue>{unit}</StatsSimpleNumericValue>
                      </div>
                    ) : null}
                  </ShareStatsContentBloc>
                  <TableWrapper>
                    {parcelSessions?.length ? (
                      <Table
                        sortAll
                        onRowHover={(id) => (id ? onRowEnter(id) : clear())}
                        columns={[
                          {
                            key: 'distance',
                            header: t('Session.distance'),
                            displayFn: ({ distance }) => formatDistance(distance, units.distance, true),
                          },
                          {
                            key: 'motor_duration',
                            header: t('Share.motor_duration'),
                            displayFn: ({ motor_duration }) => formatDuration(motor_duration, units.time, true),
                          },
                          {
                            key: 'area',
                            header: t('Session.area'),
                            displayFn: ({ area }) => formatArea(area, units.area, true),
                          },
                        ]}
                        rows={parcelSessions
                          .map((s) => ({ ...s, id: s.concerned_parcel_id }))
                          .sort((a, b) => a.distance - b.distance)}
                      />
                    ) : null}
                  </TableWrapper>
                </DataContainer>
              </ContentRow>
            </StyledShareContainer>
          ) : null}
        </ShareComponent>
      );
    }
    return (
      <ShareComponent>
        <Header Icon={Icon} equipmentModel={equipmentModel} date={date} />
        <BasicModeMapContainer>
          <Map
            fitMask="equipment,parcel,tracks"
            tracks={tracks}
            parcels={concernedParcel}
            overlays={mapOverlays}
            parcelsStyleOverrides={parcelsStyleOverrides}
          />
        </BasicModeMapContainer>
      </ShareComponent>
    );
  }
  return (
    <ShareComponent>
      {isLoading ? <TheLoader /> : null}
      {error?.errors ? (
        <ErrorWrapper>
          <img alt="karnott" src={logo} />
          <div>{t('Share.error')}</div>
        </ErrorWrapper>
      ) : null}
    </ShareComponent>
  );
}

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  img {
    max-width: 33%;
  }
`;

const ShareComponent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${colors('grey', 100)};
  font-family: ${family('body')};
`;

const StyledShareContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
`;

const IconWithLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing}px;
`;

const BasicModeMapContainer = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  display: flex;
`;

const ShareHeader = styled.div`
  width: 100%;
  height: 55px;
  display: flex;
  justify-content: space-around;
  align-items: center;
`;

const ContentRow = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

const DataContainer = styled.div`
  flex: 1;
  overflow: auto;
  min-height: 0;
  padding-block-end: ${pixelSpacing()};
`;

const TableWrapper = styled.div`
  padding-inline: ${pixelSpacing()};
`;

const ShareStatsContentBloc = styled.div`
  display: flex;
  padding: ${pixelSpacing()};
  gap: ${pixelSpacing('small')};

  > * {
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: ${colors('white')};
    border-radius: 8px;
    padding: ${pixelSpacing()};
  }
`;

export default ShareContainer;
